import { useCallback, useEffect, useState } from "react";
import type { Channel } from "stream-chat";

export const isChannelPinned = (channel?: Channel | null): boolean =>
  typeof channel?.state?.membership?.pinned_at === "string";

export const useChannelPin = (channel?: Channel | null) => {
  const [isPinned, setIsPinned] = useState(() => isChannelPinned(channel));
  const [isPinning, setIsPinning] = useState(false);

  useEffect(() => {
    if (!channel) return;

    setIsPinned(isChannelPinned(channel));

    const syncPinnedState = () => setIsPinned(isChannelPinned(channel));
    const subscriptions = [
      channel.on("member.updated", syncPinnedState),
      channel.on("member.added", syncPinnedState),
    ];

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [channel]);

  const togglePin = useCallback(async () => {
    if (!channel) return;

    const nextPinned = !isChannelPinned(channel);
    setIsPinning(true);
    setIsPinned(nextPinned);

    try {
      if (nextPinned) {
        await channel.pin();
      } else {
        await channel.unpin();
      }
    } catch (error) {
      setIsPinned(!nextPinned);
      throw error;
    } finally {
      setIsPinning(false);
    }
  }, [channel]);

  return { isPinned, isPinning, togglePin };
};
